import { Portfolio } from '@klp/portfolio/classes/Portfolio';
import type { ThunkDispatch, AnyAction } from '@reduxjs/toolkit';
import {
    createSlice,
    createAsyncThunk,
    createAction,
    isAnyOf,
} from '@reduxjs/toolkit';
import {
    saveToStorage,
    clearStorage,
    getFromStorage,
} from '@forvalter/utils/storage';
import {
    localStorageReferences,
    contextSelectorActionsReferences,
} from '../utils';
import type { Bygg } from '@forvalter/types/ForvalterCommonDAL';
import type { IContextSelectorState } from '../interfaces';
import { Region } from '@klp/portfolio/classes/Region';
import { contextSelectorApi } from '../services/rtkApi';
import { frontpageApi } from '@forvalter/frontpage/services/rtkApi';
import { ExpiryType } from '@forvalter/frontpage/classes/ExpiryType';

export interface IContextSelectorStore {
    contextSelector: ReturnType<typeof contextSelectorSlice.reducer>;
}

export const contextSelectorInitialState = (): IContextSelectorState => ({
    selectedContext: selectedContextFromStorage,
    openMineBygg: false,
    showByggGrunn: showByggGrunnFromStorage,
    selectorFocus: false,
});

const saveSelectedContextToStorage = createAsyncThunk(
    contextSelectorActionsReferences.SAVE_SELECTED_CONTEXT,
    async (context: Bygg) => {
        if (context) {
            saveToStorage(localStorageReferences.SELECTED_CONTEXT, context);
            return context;
        }
        throw new Error('No context received');
    },
);

const clearSelectedContextFromStorage = createAsyncThunk(
    contextSelectorActionsReferences.CLEAR_SELECTED_CONTEXT,
    async () => {
        Portfolio.storedSelectedPortfolio = undefined;
        Region.storedSelectedRegion = undefined;
        ExpiryType.storedSelectedExpiryType = undefined;
        clearStorage(localStorageReferences.SELECTED_CONTEXT);
        return null;
    },
);

const saveShowByggGrunnToStorage = createAsyncThunk(
    contextSelectorActionsReferences.SAVE_BYGG_GRUNN,
    async (showByggGrunn: boolean) => {
        saveToStorage(localStorageReferences.SHOW_BYGG_GRUNN, showByggGrunn);
        return showByggGrunn;
    },
);

const setAndResetSelectorFocus = createAsyncThunk(
    contextSelectorActionsReferences.SET_AND_RESET_SELECTOR_FOCUS,
    async (_, { dispatch }) => {
        dispatch(setSelectorFocus(true));
        setTimeout(() => {
            dispatch(resetSelectorFocus());
        }, 1500);
    },
);

const matchSelectedContextChanged = isAnyOf(
    contextSelectorApi.endpoints.postFavoritt.matchFulfilled,
    contextSelectorApi.endpoints.deleteFavoritt.matchFulfilled,
    clearSelectedContextFromStorage.fulfilled,
    saveSelectedContextToStorage.fulfilled,
);

const showByggGrunnFromStorage = getFromStorage(
    localStorageReferences.SHOW_BYGG_GRUNN,
) as boolean;

const selectedContextFromStorage = getFromStorage(
    localStorageReferences.SELECTED_CONTEXT,
) as IContextSelectorState['selectedContext'];

const setSelectorFocus = createAction<boolean>(
    contextSelectorActionsReferences.SET_SELECTOR_FOCUS,
);

const resetSelectorFocus = createAction(
    contextSelectorActionsReferences.RESET_SELECTOR_FOCUS,
);

const initialState = {
    selectedContext: selectedContextFromStorage,
    openMineBygg: false,
    showByggGrunn: showByggGrunnFromStorage,
    selectorFocus: false,
};

export const contextSelectorSlice = createSlice({
    name: 'contextSelector',
    initialState,
    reducers: {
        setOpenMineBygg: (state, action) => {
            state.openMineBygg = action.payload;
        },
        setSelectorFocus: (state, action) => {
            state.selectorFocus = action.payload;
        },
        resetSelectorFocus: (state) => {
            state.selectorFocus = false;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(
                saveSelectedContextToStorage.fulfilled,
                (state, action) => {
                    return {
                        ...state,
                        selectedContext: action.payload,
                    };
                },
            )
            .addCase(clearSelectedContextFromStorage.fulfilled, (state) => {
                return {
                    ...state,
                    selectedContext: undefined,
                };
            })
            .addCase(saveShowByggGrunnToStorage.fulfilled, (state, action) => {
                return {
                    ...state,
                    showByggGrunn: action.payload,
                };
            })
            .addMatcher(matchSelectedContextChanged, () => {
                frontpageApi.util.invalidateTags(['frontpageAggregate']);
            });
    },
});

export const contextSelectorActions = {
    saveSelectedContextToStorage,
    clearSelectedContextFromStorage,
    saveShowByggGrunnToStorage,
    setAndResetSelectorFocus,
    reducerActions: contextSelectorSlice.actions,
};

export const contextSelectors = ({ contextSelector }: IContextSelectorStore) =>
    contextSelector ?? contextSelectorSlice.getInitialState();

export const contextSelectorDispatch = (
    dispatch: ThunkDispatch<IContextSelectorStore, undefined, AnyAction>,
) => ({
    saveSelectedContextToStorage: (context: Bygg) =>
        dispatch(saveSelectedContextToStorage(context)),
    clearSelectedContextFromStorage: () =>
        dispatch(clearSelectedContextFromStorage()),
});
